// Drives the unseen dot on every "What's new" affordance. The comparison is between a
// build-time constant (the newest entry in the bundle) and a live Firestore watermark.

import { useCallback } from 'react'
import { releases } from '@/content/whats-new/releases'
import { PreferenceDoc, useGetPreferences } from '@/store'

/** How long an entry stays marked new when nobody opens it. Matches the one-entry-per-week cadence. */
const NEW_FOR_DAYS = 7
const MS_PER_DAY = 86_400_000

/** Parsed at local noon, like `formatEntryDate` — `new Date('2026-08-31')` is UTC midnight, a day early west of UTC. */
function daysSince(shipDate: string): number {
  return (Date.now() - new Date(`${shipDate}T12:00:00`).getTime()) / MS_PER_DAY
}

export function useWhatsNewIndicator() {
  const { state, updatePreferences } = useGetPreferences()
  const latestShipDate = releases[0].shipDate
  const lastSeen = state.settings?.whatsNewLastSeen

  // ISO dates compare correctly as strings. An absent watermark reads as caught up — it's seeded
  // on first read, and signed-out users never leave `loading`, so neither ever shows a dot.
  const unseen = !state.loading && lastSeen !== undefined && lastSeen < latestShipDate && daysSince(latestShipDate) < NEW_FOR_DAYS

  const markSeen = useCallback(() => {
    if (state.loading || lastSeen === latestShipDate) return
    void updatePreferences(PreferenceDoc.Settings, { whatsNewLastSeen: latestShipDate })
  }, [state.loading, lastSeen, latestShipDate, updatePreferences])

  return { unseen, markSeen }
}
